function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }
function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i]; return arr2; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t.return && (u = t.return(), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }
import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["className", "sx"];
import * as React from 'react';
import useEnhancedEffect from '@mui/utils/useEnhancedEffect';
import useEventCallback from '@mui/utils/useEventCallback';
import { useViews } from '../useViews';
import { isTimeView } from '../../utils/time-utils';

/**
 * Props used to handle the views that are common to all pickers.
 */

/**
 * Props used to handle the views of the pickers.
 */

/**
 * Props used to handle the value of the pickers.
 */

/**
 * Manage the views of all the pickers:
 * - Handles the view switch
 * - Handles the switch between UI views and field views
 * - Handles the focus management when switching views
 */
export const usePickerViews = _ref => {
  let props = _ref.props,
    propsFromPickerValue = _ref.propsFromPickerValue,
    additionalViewProps = _ref.additionalViewProps,
    inputRef = _ref.inputRef,
    autoFocusView = _ref.autoFocusView;
  const onChange = propsFromPickerValue.onChange,
    open = propsFromPickerValue.open,
    onSelectedSectionsChange = propsFromPickerValue.onSelectedSectionsChange,
    onClose = propsFromPickerValue.onClose;
  const views = props.views,
    openTo = props.openTo,
    onViewChange = props.onViewChange,
    disableOpenPicker = props.disableOpenPicker,
    viewRenderers = props.viewRenderers,
    timezone = props.timezone;
  const propsToForwardToView = _objectWithoutPropertiesLoose(props, _excluded);
  const _useViews = useViews({
      view: undefined,
      views,
      openTo,
      onChange,
      onViewChange,
      autoFocus: autoFocusView
    }),
    view = _useViews.view,
    setView = _useViews.setView,
    defaultView = _useViews.defaultView,
    focusedView = _useViews.focusedView,
    setFocusedView = _useViews.setFocusedView,
    setValueAndGoToNextView = _useViews.setValueAndGoToNextView;
  const _React$useMemo = React.useMemo(() => views.reduce((acc, viewForReduce) => {
      let viewMode;
      if (disableOpenPicker) {
        viewMode = 'field';
      } else if (viewRenderers[viewForReduce] != null) {
        viewMode = 'UI';
      } else {
        viewMode = 'field';
      }
      acc.viewModeLookup[viewForReduce] = viewMode;
      if (viewMode === 'UI') {
        acc.hasUIView = true;
      }
      return acc;
    }, {
      hasUIView: false,
      viewModeLookup: {}
    }), [disableOpenPicker, viewRenderers, views]),
    hasUIView = _React$useMemo.hasUIView,
    viewModeLookup = _React$useMemo.viewModeLookup;
  const timeViewsCount = React.useMemo(() => views.reduce((acc, viewForReduce) => {
    if (viewRenderers[viewForReduce] != null && isTimeView(viewForReduce)) {
      return acc + 1;
    }
    return acc;
  }, 0), [viewRenderers, views]);
  const currentViewMode = viewModeLookup[view];
  const shouldRestoreFocus = useEventCallback(() => currentViewMode === 'UI');
  const _React$useState = React.useState(currentViewMode === 'UI' ? view : null),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    popperView = _React$useState2[0],
    setPopperView = _React$useState2[1];
  if (popperView !== view && viewModeLookup[view] === 'UI') {
    setPopperView(view);
  }
  useEnhancedEffect(() => {
    // Handle case of `DateTimePicker` without time renderers
    if (currentViewMode === 'field' && open) {
      onClose();
      setTimeout(() => {
        // focusing the input before the range selection is done
        // calling `onSelectedSectionsChange` outside of timeout results in an inconsistent behavior between Safari And Chrome
        inputRef == null || inputRef.current.focus();
        onSelectedSectionsChange(view);
      });
    }
  }, [view]); // eslint-disable-line react-hooks/exhaustive-deps

  useEnhancedEffect(() => {
    if (!open) {
      return;
    }
    let newView = view;

    // If the current view is a field view, go to the last popper view
    if (currentViewMode === 'field' && popperView != null) {
      newView = popperView;
    }

    // If the current view is not the default view and both are UI views
    if (newView !== defaultView && viewModeLookup[newView] === 'UI' && viewModeLookup[defaultView] === 'UI') {
      newView = defaultView;
    }
    if (newView !== view) {
      setView(newView);
    }
    setFocusedView(newView, true);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const layoutProps = {
    views,
    view: popperView,
    onViewChange: setView
  };
  return {
    hasUIView,
    shouldRestoreFocus,
    layoutProps,
    renderCurrentView: () => {
      if (popperView == null) {
        return null;
      }
      const renderer = viewRenderers[popperView];
      if (renderer == null) {
        return null;
      }
      return renderer(_extends({}, propsToForwardToView, additionalViewProps, propsFromPickerValue, {
        views,
        timezone,
        onChange: setValueAndGoToNextView,
        view: popperView,
        onViewChange: setView,
        focusedView,
        onFocusedViewChange: setFocusedView,
        showViewSwitcher: timeViewsCount > 1,
        timeViewsCount
      }));
    }
  };
};